import { useEffect } from 'react';
import { streamChatService } from '../services/streamChatService';
import { useChatStore, getChatState } from '../store/chatStore';
import { notificationService } from '../services/notificationService';

/**
 * Hook to set up real-time Stream Chat event listeners
 * Handles events even when user is away from the chat page
 * Updates unread counts and maintains global chat state
 */
export const useStreamChatEvents = () => {
  const {
    setUnreadCount,
    incrementUnreadCount,
    updateTotalUnreadCount,
    setUserOnline,
    isConnected,
    currentUser
  } = useChatStore();

  useEffect(() => {
    if (!isConnected || !currentUser) {
      return;
    }

    try {
      const client = streamChatService.getClient();

      // Handle new messages - increment unread count and trigger notifications
      const handleNewMessage = async (event: any) => {
        if (event.channel_id && event.user?.id !== currentUser.id) {
          // Extract region information from channel data
          const channelData = event.channel?.data || {};
          const regionId = channelData.region_id;
          const channelName = event.channel?.name || event.channel?.id || 'Chat';
          // Determine channel type from custom data first, fallback to Stream Chat type
          const customChannelType = channelData.channel_type;
          const channelType = customChannelType || (event.channel?.type === 'messaging' ? 'dispatch-driver' : 'group');

          const regionContext = {
            channel: event.channel,
            channelRegion: channelData.region_name || channelData.region,
            regionId,
          };

          // Check if this message should generate a notification (includes regional filtering)
          const shouldNotify = notificationService.shouldNotifyForMessage(
            event.message,
            event.channel_id,
            {
              channel: event.channel,
              channelType,
              channelRegion: channelData.region_name || channelData.region,
              regionId,
            }
          );

          // Only increment unread count if the message passes regional filtering
          if (shouldNotify) {
            incrementUnreadCount(event.channel_id);
            updateTotalUnreadCount();

            // Trigger notification for new message
            try {
              await notificationService.notifyNewMessage(
                event.message,
                event.channel_id,
                channelName,
                channelType,
                regionContext
              );
            } catch (error) {
              console.error('[StreamChatEvents] Failed to send notification:', error);
            }
          }
        }
      };

      // Handle message read events - update unread counts when messages are marked as read
      const handleMessageRead = (event: any) => {
        if (event.channel_id && event.user?.id === currentUser.id) {
          // Only update if it's the current user who read the message
          setUnreadCount(event.channel_id, event.unread_count || 0);
          updateTotalUnreadCount();
    
        }
      };

      // Set up event listeners
      client.on('message.new', handleNewMessage);
      client.on('message.read', handleMessageRead);

      // Optional: Handle channel updates for unread counts
      const handleChannelUpdated = (event: any) => {
        if (event.channel?.id && typeof event.unread_count === 'number') {
          setUnreadCount(event.channel.id, event.unread_count);
          updateTotalUnreadCount();
        }
      };

      client.on('notification.channel_updated', handleChannelUpdated);

      // Handle user presence events - track online/offline status
      const handleUserPresenceChanged = (event: any) => {
        if (event.user?.id) {
          setUserOnline(event.user.id, event.user.online);
        }
      };

      const handleUserUpdated = (event: any) => {
        if (event.user?.id && typeof event.user.online === 'boolean') {
          setUserOnline(event.user.id, event.user.online);
        }
      };

      // Set up presence event listeners
      client.on('user.presence.changed', handleUserPresenceChanged);
      client.on('user.updated', handleUserUpdated);

      // Initialize online status for current channels' members
      const initializePresenceData = async () => {
        try {
          // Get all channels the user is a member of
          const channels = await client.queryChannels(
            { members: { $in: [currentUser.id] } },
            { last_message_at: -1 },
            { limit: 100, presence: true }
          );

          // Extract all unique member IDs and their online status
          const memberPresence = new Map<string, boolean>();
          channels.forEach(channel => {
            if (channel.state?.members) {
              Object.values(channel.state.members).forEach((member: any) => {
                if (member.user?.id && typeof member.user.online === 'boolean') {
                  memberPresence.set(member.user.id, member.user.online);
                }
              });
            }
          });

          // Update store with initial presence data
          memberPresence.forEach((online, userId) => {
            setUserOnline(userId, online);
          });

     
        } catch (error) {
          console.error('[StreamChatEvents] Failed to initialize presence data:', error);
        }
      };

      // Initialize unread counts with regional filtering
      const initializeUnreadCounts = async () => {
        try {
          // Get all channels the user is a member of
          const channels = await client.queryChannels(
            { members: { $in: [currentUser.id] } },
            { last_message_at: -1 },
            { limit: 100 }
          );

      

          // Initialize unread counts for each channel based on regional filtering
          channels.forEach(channel => {
            const channelId = channel.id;
            if (!channelId) return;

            // Get Stream Chat's unread count for this channel
            const streamUnreadCount = channel.state?.unreadCount || 0;
            // For now, we'll initialize with the Stream count and let real-time events handle filtering
            // The filtering will be applied when new messages arrive
            setUnreadCount(channelId, streamUnreadCount);
          });

          // Update total unread count
          updateTotalUnreadCount();
    
        } catch (error) {
          console.error('[StreamChatEvents] Failed to initialize unread counts:', error);
        }
      };

      // Initialize both presence data and unread counts after setting up event listeners
      initializePresenceData();
      initializeUnreadCounts();

      // Cleanup function to remove event listeners
      return () => {
        client.off('message.new', handleNewMessage);
        client.off('message.read', handleMessageRead);
        client.off('notification.channel_updated', handleChannelUpdated);
        client.off('user.presence.changed', handleUserPresenceChanged);
        client.off('user.updated', handleUserUpdated);
      };
      
    } catch (error) {
      console.error('[useStreamChatEvents] Failed to set up event listeners:', error);
    }
  }, [isConnected, currentUser?.id, setUnreadCount, incrementUnreadCount, updateTotalUnreadCount, setUserOnline]);
};